import React, { useState } from "react";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import AppChip from "../../../../common/app-chip";
import ButtonOne from "../../../../common/normal-button";
import typography from "../../../../cssUtils/typography";
import { ColorsConstants } from "../../../../cssUtils/constants";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Only these option types ever raise this notification — Submit Inquiry
// already reaches lead_email_notification via a normal outgoing edge, Chat
// With Us hands off to a live agent, and End Chat can't be selected at all.
const NOTIFIABLE_OPTION_TYPES = ["send_brochure", "open_website", "call_support"];

const EmailNotificationPreset = ({ config, handleConfigChange, nodes }) => {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  const recipientEmails = config["recipient_emails"] || [];

  const interactiveMenuNodes = (nodes || []).filter(
    (n) => n.node_type_key === "interactive_menu",
  );
  // String()-normalized: a node id saved into config before the Flow.js id-type
  // fix could still be a number, while `n.id` here is always a string now —
  // compare as strings so already-configured selections keep resolving.
  const selectedMenuNode =
    interactiveMenuNodes.find(
      (n) => String(n.id) === String(config["source_interactive_menu_node_id"]),
    ) || null;

  const notifiableOptions = (selectedMenuNode?.config?.menu_options || [])
    .map((option, index) => ({ index, option }))
    .filter(({ option }) => NOTIFIABLE_OPTION_TYPES.includes(option.type));

  const triggerIndices = config["trigger_menu_option_indices"] || [];
  const selectedOptions = notifiableOptions.filter(({ index }) =>
    triggerIndices.includes(index),
  );

  const handleMenuNodeChange = (newValue) => {
    handleConfigChange("source_interactive_menu_node_id", newValue?.id ?? null);
    handleConfigChange("trigger_menu_option_indices", []);
  };

  const handleOptionsChange = (newValue) => {
    handleConfigChange(
      "trigger_menu_option_indices",
      newValue.map(({ index }) => index),
    );
  };

  const addEmail = () => {
    const email = inputValue.trim();
    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (recipientEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      setError("This email address has already been added.");
      return;
    }

    handleConfigChange("recipient_emails", [...recipientEmails, email]);
    setInputValue("");
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleRemove = (email) => {
    handleConfigChange(
      "recipient_emails",
      recipientEmails.filter((e) => e !== email),
    );
  };

  return (
    <Stack spacing={2}>
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
        Recipient Email Addresses
      </Typography>

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <TextField
          fullWidth
          label="Add email address"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error || undefined}
        />
        <ButtonOne title="Add" variant="outlined" onClick={addEmail} />
      </Stack>

      {recipientEmails.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            border: `1px solid ${ColorsConstants.cardBorderPrimaryGrayColor}`,
            borderRadius: 2,
            p: 1.5,
          }}
        >
          {recipientEmails.map((email) => (
            <AppChip
              key={email}
              label={email}
              variant="outlined"
              onDelete={() => handleRemove(email)}
            />
          ))}
        </Box>
      )}

      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mt: 2 }}>
        Notify on Interactive Menu Selection
      </Typography>

      <Autocomplete
        options={interactiveMenuNodes}
        value={selectedMenuNode}
        onChange={(_, newValue) => handleMenuNodeChange(newValue)}
        getOptionLabel={(node) => node?.name || `Node #${node?.id}`}
        isOptionEqualToValue={(node, val) => node?.id === val?.id}
        noOptionsText="No Interactive Menu nodes in this flow yet"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Source Interactive Menu"
            helperText="Optional. Also send this notification whenever the customer picks one of the options selected below on this menu."
          />
        )}
      />

      {selectedMenuNode && (
        <Autocomplete
          multiple
          options={notifiableOptions}
          value={selectedOptions}
          onChange={(_, newValue) => handleOptionsChange(newValue)}
          getOptionLabel={({ option }) => option.button_text || "Untitled option"}
          isOptionEqualToValue={(a, b) => a.index === b.index}
          noOptionsText="This menu has no Send Brochure / Visit Website / Call Support options yet"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Trigger Menu Options"
              helperText="Send Brochure, Visit Website, and Call Support options only — End Chat can't be selected."
            />
          )}
        />
      )}
    </Stack>
  );
};

export default EmailNotificationPreset;
